/**
 * /learn/readiness-checklist — Retirement Readiness Checklist
 *
 * Interactive 50-item, 9-section checklist. Score updates live. Progress
 * persists per-browser via secureStorage so users can come back and
 * complete it over a few sittings. Full SEO metadata + JSON-LD HowTo
 * schema so search engines and AI can index every question.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Printer,
  Sprout,
  RotateCcw,
  CheckCircle2,
  Share2,
  Copy,
  Sparkles,
  Shield,
  CalendarClock,
} from "lucide-react";
import useSEO from "@/lib/useSEO";
import QuickAnswer from "@/components/QuickAnswer";
import { secureGetJSON, secureSet, secureRemove } from "@/lib/secureStorage";
import { track, EVENTS } from "@/lib/analytics";
import { appendScore, loadHistory } from "@/lib/scoreHistory";
import ScoreHistoryChart from "@/components/ScoreHistoryChart";
import EncouragementBanner from "@/components/readiness/EncouragementBanner";
import { toast } from "sonner";

const STORAGE_KEY = "centennial_readiness_v1";

const SECTIONS = [
  {
    n: "1",
    title: "My Retirement Vision",
    items: [
      "I have a target retirement age.",
      "I have thought about where I want to live.",
      "I have discussed retirement goals with my spouse or partner.",
      "I have considered travel, hobbies, family, volunteering, or part-time work.",
      "I have estimated what my retirement lifestyle may cost.",
    ],
    fields: [{ key: "target_age", label: "My target retirement age", type: "number" }],
  },
  {
    n: "2",
    title: "My Money Today",
    items: [
      "I know approximately what I spend each month.",
      "I have a working household budget.",
      "I know my current net worth.",
      "I know how much debt I owe.",
      "I have a plan for reducing high-interest or burdensome debt.",
      "I have an emergency fund.",
    ],
    fields: [{ key: "monthly_expenses", label: "My estimated monthly expenses ($)", type: "number" }],
  },
  {
    n: "3",
    title: "My Retirement Savings",
    items: [
      "I know how much I currently have saved for retirement.",
      "I contribute regularly to retirement savings.",
      "I understand my employer's retirement plan.",
      "I know whether my employer offers a matching contribution.",
      "I have reviewed my IRA, 401(k), 403(b), pension, or other retirement accounts.",
      "I know how my retirement money is invested.",
    ],
    fields: [
      { key: "retirement_savings", label: "Current retirement savings ($)", type: "number" },
      { key: "monthly_contribution", label: "Monthly retirement contribution ($)", type: "number" },
    ],
  },
  {
    n: "4",
    title: "My Retirement Income",
    items: [
      "I have reviewed my Social Security estimate.",
      "I have compared different Social Security claiming ages.",
      "I know whether I will receive a pension.",
      "I have identified other expected retirement income.",
      "I have estimated how much income I may need each month.",
      "I have compared estimated retirement income with estimated expenses.",
    ],
    fields: [
      { key: "retirement_income", label: "Estimated monthly retirement income ($)", type: "number" },
    ],
  },
  {
    n: "5",
    title: "Healthcare",
    items: [
      "I understand that healthcare needs to be part of my retirement budget.",
      "I understand the basics of Medicare eligibility and enrollment.",
      "I have considered premiums, deductibles, prescriptions, dental, vision, and other costs.",
      "I have thought about possible long-term care needs.",
      "I know how I will obtain health coverage if I retire before Medicare eligibility.",
      "I have thought about how healthcare costs may grow across a 20+ year retirement.",
    ],
    fields: [],
  },
  {
    n: "6",
    title: "My Investments",
    items: [
      "I understand the general level of investment risk I am taking.",
      "My investments reflect my age, goals, timeline, and comfort with risk.",
      "My retirement savings are reasonably diversified.",
      "I review my investments periodically.",
      "I avoid making investment decisions based only on fear, excitement, or headlines.",
    ],
    fields: [],
  },
  {
    n: "7",
    title: "Taxes & Withdrawals",
    items: [
      "I understand that different retirement accounts may be taxed differently.",
      "I have considered how taxes may affect my retirement income.",
      "I understand that some retirement accounts eventually have required distribution rules.",
      "I have thought about which accounts I may draw from first.",
      "I know when I should seek professional tax or financial guidance.",
    ],
    fields: [],
  },
  {
    n: "8",
    title: "Protecting My Family",
    items: [
      "My retirement-account beneficiaries are current.",
      "I have reviewed my will or estate documents.",
      "Important financial documents are organized.",
      "My spouse, partner, or trusted family member knows where important information is kept.",
      "I have considered powers of attorney and healthcare directives.",
      "I have a plan for protecting myself and my family from financial scams or exploitation.",
    ],
    fields: [],
  },
  {
    n: "9",
    title: "My Life Beyond the Numbers",
    items: [
      "I know what I want to retire to, not simply what I want to retire from.",
      "I have thought about how I will spend my time.",
      "I have considered family, friendships, community, and purpose.",
      "I have created a retirement bucket list.",
      "I am planning for a retirement I can enjoy — not merely afford.",
    ],
    fields: [],
  },
];

const TOTAL_ITEMS = SECTIONS.reduce((a, s) => a + s.items.length, 0);

const TIERS = [
  { min: 41, max: 50, label: "Ready & Reviewing", tone: "green",
    detail: "Wonderful work — many pieces are already in place. Keep reviewing and tuning as life changes." },
  { min: 31, max: 40, label: "Building Strongly", tone: "green",
    detail: "You've built a good foundation. A few more checks would round out your plan even further." },
  { min: 21, max: 30, label: "Time to Build", tone: "amber",
    detail: "You've made a real start. Pick the one area that would give you the most peace of mind and begin there." },
  { min: 0, max: 20, label: "You've Begun", tone: "green",
    detail: "You showed up — that's the hard part. Every honest check from here is a step toward stronger Future You." },
];

const tierFor = (score) => TIERS.find((t) => score >= t.min && score <= t.max) || TIERS[3];

export default function ReadinessChecklist() {
  useSEO({
    title: "Retirement Readiness Checklist · 50-point self-assessment · Centennial",
    description:
      "Free 50-point retirement readiness checklist. 9 sections covering vision, savings, income, healthcare, investments, taxes, estate planning, and life. Score yourself in 5 minutes.",
    canonicalPath: "/learn/readiness-checklist",
  });

  // JSON-LD structured data — HowTo + Article, so AI/search engines can index every question.
  useEffect(() => {
    const steps = SECTIONS.flatMap((s, si) =>
      s.items.map((it, ii) => ({
        "@type": "HowToStep",
        position: si * 10 + ii + 1,
        name: `${s.title} — ${it}`,
        text: it,
      })),
    );
    const jsonld = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: "Retirement Readiness Checklist — 50-point self-assessment",
      description:
        "A 9-section, 50-item free checklist to gauge your retirement readiness — vision, money today, savings, income, healthcare, investments, taxes, estate, and life beyond the numbers.",
      totalTime: "PT5M",
      step: steps,
      author: { "@type": "Organization", name: "Centennial" },
      publisher: {
        "@type": "Organization",
        name: "Centennial",
        url: "https://mycentennialplan.com",
      },
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "readiness-jsonld";
    script.text = JSON.stringify(jsonld);
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById("readiness-jsonld");
      if (existing) existing.remove();
    };
  }, []);

  const [state, setState] = useState(() =>
    secureGetJSON(STORAGE_KEY, {
      checks: {},
      fields: {},
      seed: "",
      first_step: "",
      seed_date: "",
      last_completed_at: "",  // ISO date of the last time the user actively engaged (any check)
      last_completed_score: 0,
    }),
  );
  const [showRecheck, setShowRecheck] = useState(false);

  // 90-day recheck detection: if the user last engaged 90+ days ago AND
  // had made real progress (score >= 15), pop the nudge on load.
  useEffect(() => {
    if (!state.last_completed_at || (state.last_completed_score || 0) < 15) return;
    const last = new Date(state.last_completed_at).getTime();
    if (!last) return;
    const days = Math.floor((Date.now() - last) / 86400000);
    if (days >= 90) setShowRecheck(true);
  }, [state.last_completed_at, state.last_completed_score]);

  // Persist on every change (React batches these already)
  useEffect(() => {
    secureSet(STORAGE_KEY, state);
  }, [state]);

  const toggle = (sectionIdx, itemIdx) => {
    const key = `${sectionIdx}-${itemIdx}`;
    setState((s) => {
      const nextChecks = { ...s.checks, [key]: !s.checks[key] };
      const nextScore = Object.values(nextChecks).filter(Boolean).length;
      return {
        ...s,
        checks: nextChecks,
        last_completed_at: new Date().toISOString(),
        last_completed_score: nextScore,
      };
    });
    setShowRecheck(false);
  };

  const setField = (key, value) => {
    setState((s) => ({ ...s, fields: { ...s.fields, [key]: value } }));
  };

  const score = useMemo(
    () => Object.values(state.checks).filter(Boolean).length,
    [state.checks],
  );
  const tier = tierFor(score);
  const pct = Math.round((score / TOTAL_ITEMS) * 100);

  // Score history — appended (one entry per day per kind) whenever the score changes.
  const [history, setHistory] = useState(() => loadHistory());
  useEffect(() => {
    if (score > 0) setHistory(appendScore("checklist", score, TOTAL_ITEMS));
  }, [score]);

  const reset = () => {
    if (!window.confirm("Reset every answer on this checklist?")) return;
    secureRemove(STORAGE_KEY);
    setState({ checks: {}, fields: {}, seed: "", first_step: "", seed_date: "" });
  };

  const print = () => {
    track(EVENTS.CALCULATOR_RUN, { readiness_score: score }); // reuse calc_run funnel event
    window.print();
  };

  const toneClass = {
    green: "bg-[#E8F0EB] border-[#2B5339] text-[#2B5339]",
    amber: "bg-[#FDF2ED] border-[#C26747] text-[#C26747]",
    red: "bg-[#FDF2ED] border-[#C26747] text-[#C26747]",
  }[tier.tone];

  return (
    <div className="min-h-screen bg-[#F9F8F6] print:bg-white">
      <div className="max-w-4xl mx-auto px-6 py-10 print:px-0 print:py-4">
        {/* Header */}
        <div className="print:hidden flex items-center justify-between gap-3 mb-8">
          <Link
            to="/learn"
            className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2B5339]"
          >
            <ArrowLeft size={14} /> All lessons
          </Link>
          <div className="flex items-center gap-2">
            <button
              data-testid="reset-checklist"
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white border border-[#E5E2DC] hover:border-[#C26747] hover:text-[#C26747] text-[#5C6B62]"
            >
              <RotateCcw size={12} /> Reset
            </button>
            <button
              data-testid="print-checklist"
              type="button"
              onClick={print}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white border border-[#E5E2DC] hover:border-[#2B5339] text-[#5C6B62]"
            >
              <Printer size={12} /> Print
            </button>
          </div>
        </div>

        {/* Hero */}
        <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-2">
          Retirement readiness checklist
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C2B23] leading-[1.05]">
          How ready are you<br />
          for the retirement<br />
          you want?
        </h1>
        <p className="mt-5 text-lg text-[#5C6B62] leading-relaxed max-w-2xl">
          Retirement readiness isn&apos;t about having one perfect number. It&apos;s about knowing
          where you stand — and knowing what to work on next. Take five minutes. Check what
          you&apos;ve already done. Circle the areas that need attention.
        </p>

        <QuickAnswer>
          Retirement readiness spans nine areas, not one savings number: income plan, savings,
          debt, healthcare, taxes, estate documents, housing, family communication, and purpose.
          This free 50-item checklist scores you across all nine and shows the single best next
          step — most people score 20–35 on the first pass, and that&apos;s normal.
        </QuickAnswer>

        <EncouragementBanner />

        {/* 90-day recheck banner */}
        {showRecheck && (
          <RecheckBanner
            lastAt={state.last_completed_at}
            lastScore={state.last_completed_score}
            onDismiss={() => setShowRecheck(false)}
            onReset={reset}
          />
        )}

        {/* Live score panel */}
        <div
          data-testid="score-panel"
          className={`mt-8 rounded-2xl border-2 p-5 sm:p-6 print:break-inside-avoid ${toneClass}`}
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-semibold">
                Your score
              </div>
              <div className="font-mono-num text-5xl sm:text-6xl font-medium text-[#1C2B23] leading-none mt-1">
                <span data-testid="score-value">{score}</span>
                <span className="text-[#5C6B62] text-3xl"> / {TOTAL_ITEMS}</span>
              </div>
            </div>
            <div className="text-right max-w-md">
              <div className="text-2xl font-medium text-[#1C2B23]">{tier.label}</div>
              <p className="text-sm text-[#1C2B23]/80 mt-1 leading-relaxed">{tier.detail}</p>
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-4 h-2 w-full bg-white/60 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#2B5339] transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Score history — hides itself until there are 2+ dated points */}
        <ScoreHistoryChart history={history} activeKind="checklist" />

        {/* Score-based nudge — under 30 gets a "start here" tile with two lessons */}
        {score > 0 && score < 30 && (
          <NudgeTile score={score} />
        )}

        {/* Share your score — appears after any progress */}
        {score > 0 && <ShareCard score={score} tier={tier.label} />}

        {/* Sections */}
        <div className="mt-10 space-y-8 print:mt-4">
          {SECTIONS.map((s, si) => (
            <section
              key={s.n}
              data-testid={`section-${s.n}`}
              className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-7 print:break-inside-avoid"
            >
              <div className="flex items-baseline gap-3 mb-4">
                <span className="font-mono-num text-[#2B5339] text-xl font-semibold">
                  {s.n}.
                </span>
                <h2 className="text-2xl font-medium text-[#1C2B23]">{s.title}</h2>
              </div>
              <ul className="space-y-3">
                {s.items.map((it, ii) => {
                  const key = `${si}-${ii}`;
                  const checked = !!state.checks[key];
                  return (
                    <li key={ii}>
                      <label
                        data-testid={`check-${si}-${ii}`}
                        className="flex items-start gap-3 cursor-pointer group"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(si, ii)}
                          className="mt-1 w-5 h-5 accent-[#2B5339] flex-shrink-0"
                        />
                        <span
                          className={`leading-relaxed ${
                            checked ? "text-[#5C6B62] line-through" : "text-[#1C2B23]"
                          }`}
                        >
                          {it}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
              {s.fields.length > 0 && (
                <div className="mt-4 pt-4 border-t border-[#F0EFEA] grid sm:grid-cols-2 gap-3">
                  {s.fields.map((f) => (
                    <label key={f.key} className="block">
                      <span className="text-xs text-[#5C6B62] font-medium">{f.label}</span>
                      <input
                        data-testid={`field-${f.key}`}
                        type={f.type || "text"}
                        value={state.fields[f.key] || ""}
                        onChange={(e) => setField(f.key, e.target.value)}
                        className="mt-1 w-full bg-[#F9F8F6] border border-[#E5E2DC] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2B5339]"
                      />
                    </label>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>

        {/* Plant one seed */}
        <section
          data-testid="plant-seed"
          className="mt-10 bg-[#E8F0EB] border-2 border-[#2B5339] rounded-2xl p-6 sm:p-8 print:break-inside-avoid"
        >
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-2 inline-flex items-center gap-2">
            <Sprout size={12} /> Plant one seed today
          </div>
          <h2 className="text-3xl font-medium text-[#1C2B23]">
            Choose ONE unchecked box.
          </h2>
          <p className="text-sm text-[#1C2B23]/80 mt-2 leading-relaxed">
            Don&apos;t try to fix everything at once. Pick one thing. Take one step.
          </p>
          <div className="mt-5 space-y-4">
            <label className="block">
              <span className="text-xs text-[#5C6B62] font-medium">
                The one thing I will work on next is
              </span>
              <input
                data-testid="seed-thing"
                value={state.seed || ""}
                onChange={(e) => setState((s) => ({ ...s, seed: e.target.value }))}
                className="mt-1 w-full bg-white border border-[#E5E2DC] rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#2B5339]"
              />
            </label>
            <label className="block">
              <span className="text-xs text-[#5C6B62] font-medium">
                I will take my first step by
              </span>
              <input
                data-testid="seed-step"
                value={state.first_step || ""}
                onChange={(e) => setState((s) => ({ ...s, first_step: e.target.value }))}
                className="mt-1 w-full bg-white border border-[#E5E2DC] rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#2B5339]"
              />
            </label>
            <label className="block max-w-xs">
              <span className="text-xs text-[#5C6B62] font-medium">Date</span>
              <input
                data-testid="seed-date"
                type="date"
                value={state.seed_date || ""}
                onChange={(e) => setState((s) => ({ ...s, seed_date: e.target.value }))}
                className="mt-1 w-full bg-white border border-[#E5E2DC] rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#2B5339]"
              />
            </label>
          </div>
        </section>

        {/* Centennial Coach quote */}
        <blockquote
          data-testid="coach-quote"
          className="mt-10 border-l-4 border-[#2B5339] pl-6 py-2 print:break-inside-avoid"
        >
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-2">
            Centennial Coach
          </div>
          <p className="text-xl text-[#1C2B23] leading-relaxed italic">
            Retirement readiness isn&apos;t knowing that everything will go perfectly. It&apos;s
            knowing you&apos;ve built enough toolboxes to handle life when it doesn&apos;t.
          </p>
        </blockquote>

        {/* Medication Safety integration */}
        <div
          data-testid="med-safety-readiness-card"
          className="mt-10 bg-[#1C2B23] text-white rounded-2xl p-6 print:hidden"
        >
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#9BB8A5] mb-1">
            Healthcare · Medication safety
          </div>
          <h3 className="text-xl font-medium">
            Do you maintain an accurate, current medication list?
          </h3>
          <p className="text-sm text-[#9BB8A5] mt-1">
            In an emergency, "what medications do they take?" is the first question. The free
            Centennial Medication Safety Card™ answers it — large print, private, printable.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              data-testid="med-readiness-not-yet"
              to="/medication-safety"
              className="bg-white text-[#1C2B23] rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#E8F0EB]"
            >
              Not yet — build my medication safety card
            </Link>
            <Link
              data-testid="med-readiness-yes"
              to="/medication-safety"
              className="border border-[#9BB8A5] text-white rounded-full px-5 py-2.5 text-sm hover:bg-[#2B5339]"
            >
              Yes — review my medication card
            </Link>
          </div>
        </div>

        {/* Continue building CTA */}
        <div className="mt-10 grid sm:grid-cols-2 gap-4 print:hidden">
          <Link
            to="/"
            data-testid="cta-calculator"
            className="bg-[#1C2B23] text-white rounded-2xl p-6 hover:bg-[#2B5339] transition-colors group"
          >
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-white/60 mb-1">
              Continue building your plan
            </div>
            <div className="text-2xl font-medium">Run your retirement projection →</div>
            <div className="text-sm text-white/70 mt-2">
              Free · no signup · 100-year Monte Carlo · stress tests included.
            </div>
          </Link>
          <Link
            to="/ebook/100-year-retirement-plan"
            data-testid="cta-ebook"
            className="bg-white border border-[#2B5339] text-[#1C2B23] rounded-2xl p-6 hover:bg-[#E8F0EB] transition-colors group"
          >
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-1">
              Go deeper
            </div>
            <div className="text-2xl font-medium">The 100-Year Retirement Plan ebook</div>
            <div className="text-sm text-[#5C6B62] mt-2">
              Coming soon — join the waitlist.
            </div>
          </Link>
        </div>

        {/* Disclaimer */}
        <p className="mt-10 text-xs text-[#5C6B62] leading-relaxed">
          Educational use only. This checklist is intended to help you identify retirement-planning
          topics to explore and is not individualized investment, tax, legal, Social Security, or
          healthcare advice.
        </p>

        <p className="mt-4 text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] print:mt-6">
          MyCentennialPlan™ · Plan today. Enjoy tomorrow.
        </p>
      </div>

      {/* Print rules */}
      <style>{`
        @media print {
          @page { margin: 0.4in; }
          input[type="checkbox"] { -webkit-appearance: checkbox; appearance: checkbox; }
        }
      `}</style>
    </div>
  );
}


// ───────────── 90-day recheck banner ─────────────
function RecheckBanner({ lastAt, lastScore, onDismiss, onReset }) {
  const days = Math.floor((Date.now() - new Date(lastAt).getTime()) / 86400000);
  return (
    <div
      data-testid="recheck-banner"
      className="mt-6 bg-[#FDF2ED] border border-[#C26747] rounded-2xl p-5 sm:p-6 print:hidden"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <CalendarClock size={20} className="text-[#C26747] mt-1 flex-shrink-0" />
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#C26747] mb-1">
            Time for a check-in
          </div>
          <div className="text-lg font-medium text-[#1C2B23]">
            It&apos;s been {days} days since your last review — you scored {lastScore}/50.
          </div>
          <p className="text-sm text-[#5C6B62] mt-1 leading-relaxed">
            Life moved. Money moved. Take five minutes to re-take the checklist and see how you
            compare to yourself.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="recheck-restart"
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1.5 bg-[#C26747] text-white text-sm rounded-full px-4 py-2 hover:bg-[#A5533A]"
          >
            <RotateCcw size={13} /> Start fresh
          </button>
          <button
            data-testid="recheck-dismiss"
            type="button"
            onClick={onDismiss}
            className="text-xs text-[#5C6B62] hover:text-[#1C2B23]"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}


// ───────────── Score-based nudge (< 30 / 50) ─────────────
function NudgeTile({ score }) {
  return (
    <section
      data-testid="nudge-tile"
      className="mt-6 bg-white border border-[#C26747] rounded-2xl p-6 sm:p-7 print:hidden"
    >
      <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#C26747] mb-2">
        Start here · two 5-minute lessons
      </div>
      <p className="text-[#1C2B23] leading-relaxed">
        A {score} out of 50 isn&apos;t a grade — it&apos;s a map. The two lessons below are the fastest
        way to add checkmarks and confidence at the same time.
      </p>
      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        <Link
          to="/learn/compound"
          data-testid="nudge-compound"
          className="flex items-start gap-3 bg-[#F9F8F6] border border-[#E5E2DC] rounded-xl p-4 hover:border-[#2B5339] hover:shadow-sm transition-all"
        >
          <Sparkles size={18} className="text-[#2B5339] mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium text-[#1C2B23]">Compound interest</div>
            <div className="text-xs text-[#5C6B62] mt-0.5 leading-relaxed">
              The one habit that outperforms every stock pick. 5 minutes.
            </div>
          </div>
        </Link>
        <Link
          to="/learn/emergency-fund"
          data-testid="nudge-emergency"
          className="flex items-start gap-3 bg-[#F9F8F6] border border-[#E5E2DC] rounded-xl p-4 hover:border-[#2B5339] hover:shadow-sm transition-all"
        >
          <Shield size={18} className="text-[#2B5339] mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium text-[#1C2B23]">Emergency fund</div>
            <div className="text-xs text-[#5C6B62] mt-0.5 leading-relaxed">
              Why 3 months of expenses in cash comes before anything else. 4 minutes.
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
}

// ───────────── Share-your-score card ─────────────
function ShareCard({ score, tier }) {
  // Route the shared link through the OG-wrapper endpoint so X/LinkedIn/Facebook
  // crawlers see the branded image + meta, then redirect humans to the SPA.
  const SHARE_URL = `https://mycentennialplan.com/api/share/readiness?score=${score}`;
  const text = `I scored ${score}/50 (${tier}) on the Centennial retirement readiness checklist. 5 minutes, 9 sections, free. Try it →`;

  const nativeShare = async () => {
    track(EVENTS.CALCULATOR_RUN, { readiness_share: "native", readiness_score: score });
    try {
      if (navigator.share) {
        await navigator.share({
          title: "My retirement readiness score",
          text,
          url: SHARE_URL,
        });
      } else {
        await navigator.clipboard.writeText(`${text} ${SHARE_URL}`);
        toast.success("Copied — paste it anywhere.");
      }
    } catch {
      /* user cancelled the share sheet — silent */
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${text} ${SHARE_URL}`);
      toast.success("Copied — paste it anywhere.");
      track(EVENTS.CALCULATOR_RUN, { readiness_share: "copy", readiness_score: score });
    } catch {
      toast.error("Could not copy. Long-press the text to select.");
    }
  };

  const encoded = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(SHARE_URL);
  const twitter = `https://twitter.com/intent/tweet?text=${encoded}&url=${encodedUrl}`;
  const linkedin = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
  const facebook = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encoded}`;

  return (
    <section
      data-testid="share-card"
      className="mt-4 bg-white border border-[#E5E2DC] rounded-2xl p-5 sm:p-6 print:hidden"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-1 inline-flex items-center gap-1.5">
            <Share2 size={11} /> Share your score
          </div>
          <p className="text-sm text-[#1C2B23] leading-relaxed">
            Post <span className="font-semibold">&ldquo;I scored {score}/50 on the Centennial retirement readiness checklist&rdquo;</span>.
            You might nudge a friend to run their own.
          </p>
        </div>
        <button
          data-testid="share-native"
          type="button"
          onClick={nativeShare}
          className="inline-flex items-center gap-2 bg-[#1C2B23] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#2B5339]"
        >
          <Share2 size={13} /> Share
        </button>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <a
          data-testid="share-twitter"
          href={twitter}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track(EVENTS.CALCULATOR_RUN, { readiness_share: "twitter", readiness_score: score })}
          className="inline-flex items-center gap-1.5 text-xs bg-white border border-[#E5E2DC] rounded-full px-3 py-1.5 hover:border-[#2B5339] text-[#5C6B62]"
        >
          Post on X
        </a>
        <a
          data-testid="share-linkedin"
          href={linkedin}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track(EVENTS.CALCULATOR_RUN, { readiness_share: "linkedin", readiness_score: score })}
          className="inline-flex items-center gap-1.5 text-xs bg-white border border-[#E5E2DC] rounded-full px-3 py-1.5 hover:border-[#2B5339] text-[#5C6B62]"
        >
          Post on LinkedIn
        </a>
        <a
          data-testid="share-facebook"
          href={facebook}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track(EVENTS.CALCULATOR_RUN, { readiness_share: "facebook", readiness_score: score })}
          className="inline-flex items-center gap-1.5 text-xs bg-white border border-[#E5E2DC] rounded-full px-3 py-1.5 hover:border-[#2B5339] text-[#5C6B62]"
        >
          Post on Facebook
        </a>
        <button
          data-testid="share-copy"
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1.5 text-xs bg-white border border-[#E5E2DC] rounded-full px-3 py-1.5 hover:border-[#2B5339] text-[#5C6B62]"
        >
          <Copy size={11} /> Copy text
        </button>
      </div>
    </section>
  );
}

